/* Cart bar (sticky add to cart).
   Mobil (<750px): alltid synlig. Er alternativene ikke valgt av kunden, åpnes et "bottom sheet"
   som ber kunden velge. Er alt valgt, legger knappen rett i handlekurven.
   Desktop: uendret (vises etter scroll forbi skjemaet, knappen scroller opp til alternativene). */
!function () {
  "use strict";

  const MOBILE = window.matchMedia("(max-width: 749px)");

  class CartBar extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest("product-info");
      this.form = this.section.querySelector("[data-product-form]");
      this.formWrapper = (theme.settings.productPageSticky && this.section.querySelector("[data-form-wrapper]")) || this.form;
      this.sheetOpen = false;
      this.prompting = false;
      this.chosen = new Set();
      this.lastHeight = -1;
      this.onScroll = this.onScroll.bind(this);
      this.applyMode = this.applyMode.bind(this);
      this.onAddError = this.handleProductAddError.bind(this);
      this.onKeydown = (e) => { if (e.key === "Escape") this.closeSheet(); };
    }

    connectedCallback() {
      this.addEventListener("click", this.handleClick.bind(this));
      document.addEventListener("theme:scroll", this.onScroll);
      document.addEventListener("theme:product:add-error", this.onAddError);
      MOBILE.addEventListener("change", this.applyMode);

      if (typeof subscribe === "function" && theme.PUB_SUB_EVENTS) {
        this.unsubscribeOption = subscribe(theme.PUB_SUB_EVENTS.optionValueSelectionChange, (payload) => this.handleOptionChosen(payload));
        this.unsubscribeVariant = subscribe(theme.PUB_SUB_EVENTS.variantChange, () => this.syncSheet());
      }

      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(() => this.setCartBarHeight());
        this.resizeObserver.observe(this);
      }

      this.applyMode();
      this.setCartBarHeight();
    }

    disconnectedCallback() {
      document.removeEventListener("theme:scroll", this.onScroll);
      document.removeEventListener("theme:product:add-error", this.onAddError);
      MOBILE.removeEventListener("change", this.applyMode);
      if (this.unsubscribeOption) this.unsubscribeOption();
      if (this.unsubscribeVariant) this.unsubscribeVariant();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.closeSheet();
      document.body.classList.remove("has-cart-bar-sticky");
    }

    /* Kjøres bare når skjermbredden krysser 750 px, aldri under scrolling. */
    applyMode() {
      document.body.classList.toggle("has-cart-bar-sticky", MOBILE.matches);
      if (MOBILE.matches) {
        this.classList.add("is-visible");
      } else {
        this.closeSheet();
        this.onScroll();
      }
    }

    /* Bare desktop bruker scroll; på mobil returnerer den med en gang. */
    onScroll() {
      if (MOBILE.matches || !this.formWrapper) return;
      const past = window.scrollY > this.formWrapper.offsetTop + this.formWrapper.offsetHeight;
      this.classList.toggle("is-visible", past);
    }

    setCartBarHeight() {
      const height = this.offsetHeight;
      if (height === this.lastHeight) return;
      this.lastHeight = height;
      document.documentElement.style.setProperty("--cart-bar-height", height + "px");
    }

    get mainSubmit() {
      const productForm = this.form && this.form.closest("product-form");
      return productForm ? productForm.querySelector('[type="submit"]') : null;
    }

    addToCart(e) {
      this.form.closest("product-form").onSubmitHandler(e);
    }

    /* ---------- Valg av alternativer ---------- */

    handleOptionChosen(payload) {
      const data = (payload && payload.data) || {};
      const source = (data.event && data.event.target) || data.target;
      const wrapper = source && source.closest ? source.closest("[data-option-position]") : null;
      if (wrapper) this.chosen.add(wrapper.dataset.optionPosition);
      this.syncSheet();
    }

    optionGroups() {
      return [...this.section.querySelectorAll("variant-selects [data-option-position]")].map((el) => {
        let count = el.querySelectorAll('input[type="radio"]').length;
        if (!count) count = el.querySelectorAll("[data-popout-option]").length;
        const nameEl = el.querySelector(".radio__legend__option-name") || el.querySelector(".radio__legend__label");
        return {
          el,
          pos: el.dataset.optionPosition,
          count,
          name: nameEl ? nameEl.textContent.trim().split("\n")[0].trim() : ""
        };
      });
    }

    /* Alternativer med flere verdier som kunden ennå ikke har valgt selv. */
    missingOptions() {
      return this.optionGroups().filter((g) => g.count > 1 && !this.chosen.has(g.pos));
    }

    handleClick(e) {
      const options = e.target.closest("[data-cart-bar-options]");
      if (options) {
        e.preventDefault();
        if (!MOBILE.matches) { this.scrollToTop(); return; }
        const main = this.mainSubmit;
        if (this.missingOptions().length === 0 && main && !main.hasAttribute("disabled")) {
          this.addToCart(e);
        } else {
          this.openSheet(this.missingOptions().length > 0);
        }
        return;
      }

      const add = e.target.closest("[data-cart-bar-add-to-cart]");
      if (add) {
        e.preventDefault();
        add.classList.add("is-loading");
        add.setAttribute("disabled", "disabled");
        this.addToCart(e);
      }
    }

    handleProductAddError() {
      if (this.querySelector("[data-cart-bar-add-to-cart]")) this.scrollToTop();
    }

    scrollToTop() {
      const selects = this.section.querySelector("variant-selects");
      const top = (theme.isMobile ? selects || this.form : this.section).getBoundingClientRect().top;
      window.theme.scrollTo(theme.isMobile ? top - 10 : top);
    }

    /* ---------- Bottom sheet med produktalternativer (mobil) ---------- */

    get holder() {
      return this.section.querySelector(".product__form__holder");
    }

    buildSheetChrome(holder) {
      if (holder.querySelector(".cart-bar-sheet__head")) return;

      const head = document.createElement("div");
      head.className = "cart-bar-sheet__head";
      const title = document.createElement("span");
      title.className = "cart-bar-sheet__title";
      title.textContent = this.dataset.sheetTitle || "";
      const close = document.createElement("button");
      close.type = "button";
      close.className = "cart-bar-sheet__close";
      close.setAttribute("aria-label", this.dataset.closeLabel || "Close");
      close.innerHTML = "&times;";
      close.addEventListener("click", () => this.closeSheet());
      head.append(title, close);

      const foot = document.createElement("div");
      foot.className = "cart-bar-sheet__foot";
      const msg = document.createElement("p");
      msg.className = "cart-bar-sheet__msg";
      msg.setAttribute("role", "alert");
      const add = document.createElement("button");
      add.type = "button";
      add.className = "btn " + (this.dataset.buttonClass || "btn--primary btn--solid") + " btn--large cart-bar-sheet__add";
      add.addEventListener("click", (e) => {
        e.preventDefault();
        const missing = this.missingOptions();
        if (missing.length) {
          this.prompting = true;
          this.syncSheet();
          return;
        }
        if (add.disabled) return;
        this.closeSheet();
        this.addToCart(e);
      });
      foot.append(msg, add);

      holder.prepend(head);
      holder.append(foot);
    }

    openSheet(prompt) {
      const holder = this.holder;
      if (!holder) { this.scrollToTop(); return; }
      if (this.sheetOpen) return;

      this.sheetHolder = holder;
      this.placeholder = document.createElement("div");
      this.placeholder.className = "cart-bar-sheet__placeholder";
      this.placeholder.style.height = holder.offsetHeight + "px";
      holder.before(this.placeholder);

      this.buildSheetChrome(holder);

      this.backdrop = document.createElement("div");
      this.backdrop.className = "cart-bar-sheet__backdrop";
      this.backdrop.addEventListener("click", () => this.closeSheet());
      document.body.appendChild(this.backdrop);
      requestAnimationFrame(() => this.backdrop && this.backdrop.classList.add("is-visible"));

      holder.classList.add("is-sheet-open");
      document.documentElement.classList.add("cart-bar-sheet-open");
      document.addEventListener("keydown", this.onKeydown);
      this.sheetOpen = true;
      this.prompting = !!prompt;
      this.syncSheet();
    }

    closeSheet() {
      if (!this.sheetOpen) return;
      this.sheetOpen = false;
      this.prompting = false;
      if (this.sheetHolder) {
        this.sheetHolder.classList.remove("is-sheet-open");
        this.sheetHolder.querySelectorAll(".cart-bar-needs-choice").forEach((el) => el.classList.remove("cart-bar-needs-choice"));
      }
      if (this.placeholder) this.placeholder.remove();
      if (this.backdrop) this.backdrop.remove();
      this.placeholder = null;
      this.backdrop = null;
      document.documentElement.classList.remove("cart-bar-sheet-open");
      document.removeEventListener("keydown", this.onKeydown);
    }

    /* Knapp, melding og markering i sheet holdes i synk med valg og hovedknappen. */
    syncSheet() {
      if (!this.sheetOpen || !this.sheetHolder) return;
      const add = this.sheetHolder.querySelector(".cart-bar-sheet__add");
      const msg = this.sheetHolder.querySelector(".cart-bar-sheet__msg");
      const main = this.mainSubmit;
      if (!add || !msg || !main) return;

      const mainText = main.querySelector("[data-add-to-cart-text]");
      const node = mainText && mainText.firstChild;
      let label = node ? node.textContent.trim() : "";
      const disabled = main.hasAttribute("disabled");
      const missing = this.missingOptions();

      this.sheetHolder.querySelectorAll(".cart-bar-needs-choice").forEach((el) => el.classList.remove("cart-bar-needs-choice"));

      if (missing.length && this.prompting) {
        const select = this.dataset.selectLabel || "";
        msg.textContent = (select + " " + missing[0].name.toLowerCase()).trim();
        missing[0].el.classList.add("cart-bar-needs-choice");
        missing[0].el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } else {
        msg.textContent = "";
        if (!missing.length) this.prompting = false;
      }

      if (!disabled && !missing.length) {
        try {
          const json = this.section.querySelector("variant-selects [data-selected-variant]");
          const variant = json ? JSON.parse(json.innerHTML) : null;
          if (variant && variant.price && window.theme.formatMoney) {
            const money = String(window.theme.formatMoney(variant.price, theme.moneyFormat)).replace(/<[^>]*>/g, "");
            label += " – " + money;
          }
        } catch (err) { /* uten pris i knappen */ }
      }

      add.textContent = label;
      add.disabled = disabled && !missing.length;
    }
  }

  customElements.get("cart-bar") || customElements.define("cart-bar", CartBar);
}();
